import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceIcon from '@mui/icons-material/Place';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Divider,
  LinearProgress,
} from '@mui/material';
import { memo, useMemo } from 'react';

import type { Meeting, AgendaItem } from '@penticton-dev-events/shared';

export type EventAgendaProps = {
  readonly meeting: Meeting;
  readonly expanded?: boolean;
};

/**
 * EventAgenda displays the meeting agenda in a structured, timeline format.
 * Features expandable sections, duration tracking, and presenter information.
 */
const EventAgenda = memo<EventAgendaProps>(({ meeting, expanded = false }) => {
  const agenda = useMemo(() => {
    if (!meeting.agenda || meeting.agenda.length === 0) return [];
    return [...meeting.agenda].sort((a, b) => a.order - b.order);
  }, [meeting.agenda]);

  const agendaStats = useMemo(() => {
    const totalDuration = agenda.reduce((sum, item) => sum + (item.duration || 0), 0);
    const itemsWithDuration = agenda.filter(item => item.duration).length;
    const itemsWithPresenter = agenda.filter(item => item.presenter).length;

    return {
      totalDuration,
      itemsWithDuration,
      itemsWithPresenter,
      hasTimedItems: itemsWithDuration > 0,
    };
  }, [agenda]);

  if (!agenda.length) {
    return null;
  }

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const calculateTimeProgress = (currentIndex: number): number => {
    if (!agendaStats.hasTimedItems) return 0;

    const completedDuration = agenda
      .slice(0, currentIndex)
      .reduce((sum, item) => sum + (item.duration || 0), 0);

    return agendaStats.totalDuration > 0
      ? (completedDuration / agendaStats.totalDuration) * 100
      : 0;
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={3}>
          {/* Header */}
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              display="flex"
              alignItems="center"
              gap={1}
            >
              <ScheduleIcon color="primary" />
              Agenda ({agenda.length} items)
            </Typography>

            {/* Agenda Summary */}
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {/* Event Type Indicator */}
              <Chip
                icon={
                  meeting.eventType === 'in-person' ? (
                    <PlaceIcon />
                  ) : meeting.eventType === 'online' ? (
                    <VideocamIcon />
                  ) : undefined
                }
                label={
                  meeting.eventType === 'in-person'
                    ? 'In-Person'
                    : meeting.eventType === 'online'
                      ? 'Online'
                      : 'Hybrid'
                }
                size="small"
                color={
                  meeting.eventType === 'in-person'
                    ? 'info'
                    : meeting.eventType === 'online'
                      ? 'secondary'
                      : 'warning'
                }
                variant="outlined"
              />
              {agendaStats.totalDuration > 0 && (
                <Chip
                  label={`Total: ${formatDuration(agendaStats.totalDuration)}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {agendaStats.itemsWithPresenter > 0 && (
                <Chip
                  label={`${agendaStats.itemsWithPresenter} presenter${agendaStats.itemsWithPresenter === 1 ? '' : 's'}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Agenda Items */}
          <Stack spacing={2}>
            {agenda.map((item, index) => (
              <Box key={item.id}>
                {/* Progress indicator for timed agenda */}
                {agendaStats.hasTimedItems && index > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={calculateTimeProgress(index)}
                      sx={{ height: 2, borderRadius: 1 }}
                    />
                  </Box>
                )}

                <Card
                  variant="outlined"
                  sx={{
                    bgcolor: 'action.hover',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'action.selected',
                      transform: 'translateY(-1px)',
                      boxShadow: 1,
                    },
                  }}
                >
                  <CardContent sx={{ pb: 2 }}>
                    <Stack spacing={2}>
                      {/* Item Header */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={2}
                      >
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="medium"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              wordBreak: 'break-word',
                            }}
                          >
                            <Chip
                              label={index + 1}
                              size="small"
                              color="primary"
                              sx={{ minWidth: '24px', height: '24px' }}
                            />
                            {item.title}
                          </Typography>
                        </Box>

                        {/* Duration Badge */}
                        {item.duration && (
                          <Chip
                            label={formatDuration(item.duration)}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            icon={<ScheduleIcon />}
                          />
                        )}
                      </Box>

                      {/* Description */}
                      {item.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {item.description}
                        </Typography>
                      )}

                      {/* Presenter */}
                      {item.presenter && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            <strong>Presenter:</strong> {item.presenter}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Stack>

          {/* Footer Summary */}
          {agendaStats.totalDuration > 0 && (
            <>
              <Divider />
              <Box textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  Estimated total duration:{' '}
                  <strong>{formatDuration(agendaStats.totalDuration)}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Times are approximate and may vary based on discussion
                </Typography>
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
});

EventAgenda.displayName = 'EventAgenda';

export default EventAgenda;
